import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { adminGet, adminPost, adminPut, adminDelete } from "../../../api/adminClient";
import Icon from "../../../components/AppIcon";
import { useToast } from "../../../hooks/use-toast";
import ConfirmationModal from "../../../components/ui/ConfirmationModal";
import { InlineLoader } from "../../../components/ui/Loader";

const SYSTEM_ROLES = ["SUPER_ADMIN", "ADMIN", "USER"];

const RolesManagement = () => {
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [managePermModalOpen, setManagePermModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedRolePermissions, setSelectedRolePermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [formData, setFormData] = useState({ name: "", description: "", is_active: true });
  const [formErrors, setFormErrors] = useState({});
  const navigate = useNavigate();

  const loadRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminGet("/admin/settings/roles?include_inactive=true");
      setRoles(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.status !== 401 && !e?.isSessionExpired) {
        setError(e?.message || "Failed to load roles");
      }
      if (e?.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const data = await adminGet("/admin/settings/permissions?include_inactive=true");
      setPermissions(Array.isArray(data) ? data : []);
    } catch (_) {}
  };

  const loadRolePermissions = async (roleId) => {
    try {
      const data = await adminGet(`/admin/settings/roles/${roleId}/permissions`);
      return Array.isArray(data) ? data : [];
    } catch (_) {
      return [];
    }
  };

  useEffect(() => {
    loadRoles();
    loadPermissions();
  }, []);

  const handleViewRole = async (role) => {
    setSelectedRole(role);
    const perms = await loadRolePermissions(role.role_id);
    setSelectedRolePermissions(perms);
    setViewModalOpen(true);
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name ?? "",
      description: role.description ?? "",
      is_active: role.is_active ?? true,
    });
    setFormErrors({});
    setEditModalOpen(true);
  };

  const handleManagePermissions = async (role) => {
    setSelectedRole(role);
    const perms = await loadRolePermissions(role.role_id);
    setSelectedPermissions(perms.map((p) => p.permission_id));
    setManagePermModalOpen(true);
  };

  const handleDeleteRole = (role) => {
    setSelectedRole(role);
    setDeleteModalOpen(true);
  };

  const handleTogglePermission = (permissionId) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId) ? prev.filter((id) => id !== permissionId) : [...prev, permissionId]
    );
  };

  const validateRoleName = (name) => {
    const v = (name || "").trim();
    if (!v) return "Role name is required";
    if (v.length < 2) return "Role name must be at least 2 characters";
    const normalized = v.toUpperCase().replace(/\s+/g, "_");
    if (!/^[A-Z][A-Z0-9_]*$/.test(normalized)) {
      return "Use UPPERCASE with letters, numbers, underscores (e.g. SUPER_ADMIN, MANAGER)";
    }
    return null;
  };

  const handleSaveRole = async () => {
    const err = {};
    const nameErr = validateRoleName(formData.name);
    if (nameErr) err.name = nameErr;
    if (formData.description && formData.description.length > 500) err.description = "Description max 500 characters";
    setFormErrors(err);
    if (Object.keys(err).length) return;
    setEditModalOpen(false);
    setLoading(true);
    setFormErrors({});
    try {
      const nameNormalized = formData.name.trim().toUpperCase().replace(/\s+/g, "_");
      const payload = {
        name: nameNormalized,
        description: formData.description?.trim() || null,
        is_active: Boolean(formData.is_active),
      };
      if (selectedRole) {
        await adminPut(`/admin/settings/roles/${selectedRole.role_id}`, payload);
      } else {
        await adminPost("/admin/settings/roles", payload);
      }
      setSelectedRole(null);
      setFormData({ name: "", description: "", is_active: true });
      success(selectedRole ? "Role updated successfully" : "Role created successfully");
      loadRoles();
    } catch (e) {
      const fieldErrs = {};
      if (e?.response?.detail && Array.isArray(e.response.detail)) {
        e.response.detail.forEach((d) => {
          const field = d?.loc?.[d.loc.length - 1] || "name";
          if (field !== "body") fieldErrs[field] = d?.msg || d?.message;
        });
      }
      if (e?.response?.data?.errors) {
        Object.assign(fieldErrs, e.response.data.errors);
      }
      if (Object.keys(fieldErrs).length === 0) fieldErrs._submit = e?.message || "Save failed";
      setFormErrors(fieldErrs);
      setEditModalOpen(true);
      if (e?.status !== 401 && !e?.isSessionExpired) showError(e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;
    setManagePermModalOpen(false);
    setLoading(true);
    try {
      await adminPut(`/admin/settings/roles/${selectedRole.role_id}/permissions`, {
        permission_ids: selectedPermissions,
      });
      setSelectedRole(null);
      setSelectedPermissions([]);
      success("Permissions updated successfully");
      loadRoles();
    } catch (e) {
      if (e?.status !== 401 && !e?.isSessionExpired) {
        setFormErrors((prev) => ({ ...prev, _submit: e?.message || "Save failed" }));
        setManagePermModalOpen(true);
        showError(e?.message || "Save failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedRole) return;
    setDeleteModalOpen(false);
    setLoading(true);
    try {
      await adminDelete(`/admin/settings/roles/${selectedRole.role_id}`);
      setSelectedRole(null);
      success("Role deleted successfully");
      loadRoles();
    } catch (e) {
      if (e?.status !== 401 && !e?.isSessionExpired) showError(e?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const getPermissionCategory = (module) => (module ? module.replace(/_/g, " ") : "Other");

  const groupedPermissions = permissions.reduce((acc, perm) => {
    const cat = perm.module || "other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(perm);
    return acc;
  }, {});

  const filteredRoles = roles.filter(
    (r) =>
      (r.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.description || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Roles</h1>
          <p className="text-text-secondary mt-1 text-sm sm:text-base">Manage roles and assign permissions</p>
        </div>
        <div className="bg-card rounded-xl shadow-lg border border-border overflow-hidden">
          <div className="flex flex-col items-center justify-center py-12">
            <InlineLoader text="Loading roles…" size="large" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Roles</h1>
          <p className="text-text-secondary mt-1 text-sm sm:text-base">Manage roles and assign permissions</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setSelectedRole(null);
            setFormData({ name: "", description: "", is_active: true });
            setFormErrors({});
            setEditModalOpen(true);
          }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 shadow-md hover:shadow-lg transition-shadow shrink-0"
        >
          <Icon name="Plus" size={20} />
          Create Role
        </button>
      </motion.div>

      {error && (
        <div className="p-3 rounded-xl bg-error/10 border border-error/20 text-error text-sm">{error}</div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="bg-card rounded-xl shadow-lg border border-border p-4 sm:p-6"
      >
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
          <div className="relative w-full sm:max-w-xl flex-1">
            <Icon
              name="Search"
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search roles…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-text-secondary">
            <Icon name="Shield" size={18} />
            <span>{filteredRoles.length} role{filteredRoles.length !== 1 ? "s" : ""}</span>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="bg-card rounded-xl shadow-lg border border-border overflow-hidden"
      >
        <div className="px-4 sm:px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-text-primary">All Roles</h2>
          <p className="text-sm text-text-secondary mt-0.5">View and manage system roles</p>
        </div>
        <div className="p-4 sm:p-6">
          {filteredRoles.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Icon name="Shield" size={28} className="text-primary" />
              </div>
              <p className="text-text-secondary mb-4">
                {searchTerm ? "No roles found matching your search." : "No roles found."}
              </p>
              {!searchTerm && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedRole(null);
                    setFormData({ name: "", description: "", is_active: true });
                    setEditModalOpen(true);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium shadow-md hover:shadow-lg transition-shadow"
                >
                  <Icon name="Plus" size={20} />
                  Create First Role
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="px-4 py-3 text-sm font-medium text-text-primary">Role Name</th>
                    <th className="px-4 py-3 text-sm font-medium text-text-primary">Description</th>
                    <th className="px-4 py-3 text-sm font-medium text-text-primary">Status</th>
                    <th className="px-4 py-3 text-sm font-medium text-text-primary text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRoles.map((role) => (
                    <tr key={role.role_id} className="border-b border-border last:border-0">
                      <td className="px-4 py-3 text-text-primary font-medium">{role.name}</td>
                      <td className="px-4 py-3 text-text-secondary">{role.description || "—"}</td>
                      <td className="px-4 py-3">
                        <span
                          className={role.is_active ? "text-success" : "text-text-secondary"}
                        >
                          {role.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleViewRole(role)}
                            title="View"
                            className="p-2 rounded-lg hover:bg-muted text-primary transition-colors"
                          >
                            <Icon name="Eye" size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleManagePermissions(role)}
                            title="Permissions"
                            className="p-2 rounded-lg hover:bg-muted text-primary transition-colors"
                          >
                            <Icon name="Key" size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEditRole(role)}
                            title="Edit"
                            className="p-2 rounded-lg hover:bg-muted text-primary transition-colors"
                          >
                            <Icon name="Edit" size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteRole(role)}
                            disabled={SYSTEM_ROLES.includes((role.name || "").toUpperCase())}
                            title={SYSTEM_ROLES.includes((role.name || "").toUpperCase()) ? "System roles cannot be deleted" : "Delete role"}
                            className="p-2 rounded-lg hover:bg-muted text-error transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                          >
                            <Icon name="Trash2" size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>

      {/* View Role Modal */}
      {viewModalOpen && selectedRole && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setViewModalOpen(false)}>
          <div className="bg-card rounded-xl shadow-lg border border-border p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto admin-layout text-text-primary" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-text-primary">Role: {selectedRole.name}</h3>
              <button type="button" onClick={() => setViewModalOpen(false)} className="p-2 rounded-lg hover:bg-muted">
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-text-secondary mb-1">Status</p>
                <p className="font-medium">{selectedRole.is_active ? "Active" : "Inactive"}</p>
              </div>
              <div>
                <p className="text-sm text-text-secondary mb-1">Description</p>
                <p className="text-sm">{selectedRole.description || "—"}</p>
              </div>
              <div className="pt-4 border-t">
                <p className="text-sm font-medium mb-3">Permissions ({selectedRolePermissions.length})</p>
                {selectedRolePermissions.length === 0 ? (
                  <p className="text-sm text-text-secondary">No permissions assigned</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {Object.entries(groupedPermissions).map(([cat, perms]) => {
                      const rolePerms = selectedRolePermissions.filter((rp) =>
                        perms.some((p) => p.permission_id === rp.permission_id)
                      );
                      if (rolePerms.length === 0) return null;
                      return (
                        <div key={cat} className="p-3 bg-muted rounded-lg">
                          <p className="text-xs font-medium text-text-secondary mb-2">{getPermissionCategory(cat)}</p>
                          <div className="flex flex-wrap gap-2">
                            {rolePerms.map((p) => (
                              <span key={p.permission_id} className="px-2 py-1 text-xs rounded border border-border">
                                {p.name || p.code}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit/Create Role Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setEditModalOpen(false)}>
          <div className="bg-card rounded-xl shadow-lg border border-border p-6 w-full max-w-lg admin-layout text-text-primary" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-text-primary">{selectedRole ? "Edit Role" : "Create Role"}</h3>
              <button type="button" onClick={() => setEditModalOpen(false)} className="p-2 rounded-lg hover:bg-muted">
                <Icon name="X" size={20} />
              </button>
            </div>
            {formErrors._submit && (
              <div className="mb-4 p-3 rounded-lg bg-error/10 border border-error/20 text-error text-sm">{formErrors._submit}</div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Role Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    if (formErrors.name) setFormErrors((p) => ({ ...p, name: null }));
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text-primary"
                  placeholder="e.g. SUPER_ADMIN, MANAGER, SALES_REP"
                />
                {formErrors.name && <p className="text-xs text-error mt-1">{formErrors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text-primary"
                />
                {formErrors.description && <p className="text-xs text-error mt-1">{formErrors.description}</p>}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-border"
                />
                <span className="text-sm text-text-primary">Active</span>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setEditModalOpen(false)} className="px-4 py-2 rounded-lg border border-border hover:bg-muted">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveRole}
                  disabled={!formData.name?.trim()}
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  {selectedRole ? "Update Role" : "Create Role"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Permissions Modal */}
      {managePermModalOpen && selectedRole && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setManagePermModalOpen(false)}>
          <div className="bg-card rounded-xl shadow-lg border border-border p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto admin-layout text-text-primary" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-text-primary">Manage Permissions - {selectedRole.name}</h3>
              <button type="button" onClick={() => setManagePermModalOpen(false)} className="p-2 rounded-lg hover:bg-muted">
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-3 bg-muted rounded-lg mb-4">
              <p className="text-sm text-text-secondary">Role: {selectedRole.name}</p>
              <p className="text-xs text-text-secondary mt-1">{selectedRole.description || "No description"}</p>
            </div>
            <div className="max-h-[400px] overflow-y-auto space-y-4 border rounded-lg p-4">
              {Object.entries(groupedPermissions)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([cat, perms]) => (
                  <div key={cat} className="space-y-2">
                    <p className="text-sm font-medium text-text-primary pb-2 border-b border-border">
                      {getPermissionCategory(cat)} ({perms.length})
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {perms.map((perm) => {
                        const isSelected = selectedPermissions.includes(perm.permission_id);
                        return (
                          <div
                            key={perm.permission_id}
                            className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                              isSelected ? "bg-primary/10 border-primary" : "hover:bg-muted/50"
                            }`}
                            onClick={() => handleTogglePermission(perm.permission_id)}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleTogglePermission(perm.permission_id)}
                              className="rounded border-border"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-mono font-medium break-all">{perm.name}</p>
                              {perm.description && (
                                <p className="text-xs text-text-secondary line-clamp-1">{perm.description}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
            </div>
            <div className="flex justify-between items-center pt-4 border-t mt-4">
              <p className="text-sm text-text-secondary">
                Selected: <span className="font-medium text-text-primary">{selectedPermissions.length}</span> permission(s)
              </p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setManagePermModalOpen(false)} className="px-4 py-2 rounded-lg border border-border hover:bg-muted">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSavePermissions}
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90"
                >
                  Save Permissions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteModalOpen && !!selectedRole}
        title="Delete Role"
        message={`Are you sure you want to delete the role "${selectedRole?.name}"? Admins assigned to this role will need to be reassigned.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => { setDeleteModalOpen(false); setSelectedRole(null); }}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </div>
  );
};

export default RolesManagement;
